import { guid, Records } from "@buildwithflux/shared";
import { chain, cloneDeep, findKey, mapValues, pullAll, values } from "lodash";
import { doDeleteSubjects, handleAddElements } from "../document-actions";
import { ElementHelper, SchematicAutoPlacementByPartType } from "../elements";
function autoPositionElementsWithoutWires(document, elementUids, componentGroups = {}) {
    const targetElementsByGroup = chain(document.elements)
        .pick(elementUids)
        .values()
        .groupBy((e) => findKey(componentGroups, (c) => c.includes(e.label ?? "")) ?? "")
        .value();
    const allElementsByGroup = chain(document.elements)
        .values()
        .groupBy((e) => findKey(componentGroups, (c) => c.includes(e.label ?? "")) ?? "")
        .value();
    const allElementUidsByGroup = mapValues(allElementsByGroup, (elems) => elems.map((e) => e.uid));
    // Track yet-to-be-positioned elements to avoid collisions with them
    const yetToBePositionedUids = [...elementUids];
    for (const [groupName, groupElements] of Object.entries(targetElementsByGroup)) {
        const groupElementUids = groupElements.map((e) => e.uid);
        // Only ignore elements that haven't been, and aren't about to be, placed
        pullAll(yetToBePositionedUids, groupElementUids);
        const positioner = new SchematicAutoPlacementByPartType(document, allElementUidsByGroup, groupName, groupElementUids, {
            // Generous spacing, especially horizontal, to allow for portals between pins of
            // side-by-side ICs, common with autopilot
            partTypeSpacing: 200,
            verticalSpacing: 60,
            // Biggish difference between partTypeSpacing and groupSpacing, so groups are
            // easily distinguishable
            groupSpacing: 500,
            elementUidsToIgnore: yetToBePositionedUids,
        });
        positioner.runLayout();
        const updatedElements = positioner.getUpdatedElementData();
        for (const [elementUid, updatedElement] of Object.entries(updatedElements)) {
            const element = document.elements[elementUid];
            if (!element)
                continue;
            element.symbol_placements = updatedElement.symbol_placements;
            element.updated_at = new Date().getTime();
        }
    }
}
/** Applies a components action by adding or removing components */
export function applyComponentsAction(action, document, componentGroups = {}, actionContext) {
    const touchedElementUids = [];
    // Step 1: Identify all elements to remove FIRST, using filter() to catch all instances
    // of duplicate designators (e.g. Net Portals intentionally share designators).
    const elementsToDelete = action.componentsToRemove.flatMap((component) => {
        const matchingElements = values(document.elements).filter((element) => element.label?.toLowerCase().trim() === component.componentDesignator.toLowerCase().trim());
        return matchingElements.map((element) => ({
            designator: component.componentDesignator,
            uid: element.uid,
        }));
    });
    // Step 2: Actually remove the elements from the document before adding new ones.
    // This ensures that designators from removed components are freed up for reuse,
    // which is critical for replace operations (remove K1-K8, add K1-K8).
    if (elementsToDelete.length > 0) {
        doDeleteSubjects(document, elementsToDelete.map((e) => e.uid), actionContext.logger);
    }
    // Step 3: Build the designator sequencer from the post-removal document state,
    // so that requested designators from removed components can be reused.
    const designatorSequencer = ElementHelper.createDesignatorSequencer(document.elements);
    const elementsToAdd = [];
    const designatorWarnings = [];
    const newElementGroups = {};
    componentGroups = cloneDeep(componentGroups); // Don't mutate caller's data.
    for (const component of action.componentsToAdd) {
        try {
            const quantity = Math.max(1, Math.floor(component.quantity ?? 1));
            for (let i = 0; i < quantity; i++) {
                const newElement = createComponent(component, component.componentPropertiesToAddOrEdit ?? [], component.partVersionData, designatorSequencer.findNextDesignatorFromPartVersionData, designatorSequencer.tryReserveDesignator, designatorWarnings, actionContext.userUid);
                if (component.group) {
                    newElementGroups[newElement.uid] = component.group;
                    componentGroups[component.group] ??= [];
                    componentGroups[component.group]?.push(newElement.label ?? "");
                }
                elementsToAdd.push(newElement);
                touchedElementUids.push(newElement.uid);
            }
        }
        catch (err) {
            if ("captureError" in actionContext.logger) {
                actionContext.logger.captureError(err);
            }
            else if ("error" in actionContext.logger) {
                actionContext.logger.error(err, `Error creating component to add`);
            }
            continue;
        }
    }
    if (elementsToAdd.length > 0) {
        handleAddElements(document, {
            elements: elementsToAdd, // Note: positions get adjusted with autoPositioning below
            dropPosition: { x: 0, y: 0 },
            selectElements: false,
            inPcbEditor: false, // TODO
        });
    }
    if (elementsToAdd.length > 0) {
        autoPositionElementsWithoutWires(document, elementsToAdd.map((e) => e.uid), componentGroups);
    }
    return {
        componentsAdded: elementsToAdd.map((e) => ({
            uid: e.uid,
            group: newElementGroups[e.uid],
            designator: e.label ?? "",
        })),
        componentsRemoved: elementsToDelete,
        designatorWarnings: designatorWarnings.length > 0 ? designatorWarnings : undefined,
        touchedElementUids,
    };
}
function createComponent(component, properties, partVersionToBeAdded, findNextDesignatorFromPartVersionData, tryReserveDesignator, designatorWarnings, userUid) {
    const schematicPosition = { x: 0, y: 0 };
    let designator;
    if (component.componentDesignator && tryReserveDesignator(component.componentDesignator)) {
        designator = component.componentDesignator;
    }
    else {
        designator = findNextDesignatorFromPartVersionData(partVersionToBeAdded);
        if (component.componentDesignator && component.componentDesignator !== designator) {
            designatorWarnings.push({
                requested: component.componentDesignator,
                assigned: designator,
            });
        }
    }
    const newElementFromPart = ElementHelper.createNewElementData(partVersionToBeAdded, schematicPosition, userUid, designator);
    newElementFromPart.properties = {
        ...newElementFromPart.properties,
        ...Object.fromEntries((properties ?? []).map((property) => {
            const existingProperty = Records.findValue(newElementFromPart.properties, (existingProperty) => existingProperty.name === property.name);
            let prop;
            if (existingProperty) {
                prop = {
                    ...existingProperty,
                    value: property.value,
                    unit: property.unit || existingProperty.unit,
                };
            }
            else {
                prop = {
                    uid: guid(),
                    name: property.name,
                    value: property.value,
                    unit: property.unit ?? undefined,
                };
            }
            return [prop.uid, prop];
        })),
    };
    return newElementFromPart;
}
//# sourceMappingURL=applyComponentsAction.js.map